import React, { useState, useEffect } from "react";
import axios from "axios";
import { useDispatch, connect } from "react-redux";
import clsx from "clsx";
import { injectIntl } from "react-intl";
import {
  Snackbar,
  SnackbarContent,
  IconButton,
  makeStyles,
  colors,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import ErrorIcon from "@material-ui/icons/Error";
import InfoIcon from "@material-ui/icons/Info";
import Layout from "app/hoc/Layout";
import Ticket from "./components/Ticket";
import * as auth from "../Auth/_redux/authRedux";
import * as ticketRedux from "./_redux/ticketRedux";
import Payment from "./modals/Payment";
import EditTicket from "./modals/EditTicket";
import Print from "./modals/Print";
import CancelTicket from "./modals/CancelTicket";
import PaidWinningAmount from "./modals/PaidWinningAmount";
import useScanner from "../../hooks/useScanner";

const variantIcon = {
  success: CheckCircleIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  info: InfoIcon,
};

function Tickets() {
  const useStyles1 = makeStyles((theme) => ({
    success: {
      backgroundColor: green[600],
    },
    error: {
      backgroundColor: theme.palette.error.dark,
    },
    info: {
      backgroundColor: theme.palette.primary.main,
    },
    warning: {
      backgroundColor: amber[700],
    },
    icon: {
      fontSize: 20,
    },
    iconVariant: {
      opacity: 0.9,
      marginRight: theme.spacing(1),
    },
    message: {
      display: "flex",
      alignItems: "center",
    },
  }));

  const [skip] = useState(0);
  const [take] = useState(10);
  const [sortField] = useState("");
  const [orderBy] = useState(false);
  const [ticket, setTicket] = useState({});
  const [chooseGroupId, setChooseGroupId] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [showPayment, setShowPayment] = useState(false);
  const [showPaidWinning, setShowPaidWinning] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showCancelTicket, setShowCancelTicket] = useState(false);
  const [showTicket, setShowTicket] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("");
  const [barcode, setBarcode] = useState("");
  const [isPrintClosed, setIsPrintClosed] = useState(false);

  const { handleStopTime, isScanner, handleChange } = useScanner(barcode);
  console.log(isScanner);
  const handleBarcode = (e) => {
    if (e.target.value.length === 17) {
      handleChange(e, () => setBarcode(e.target.value[16]));
      return;
    }
    handleChange(e, () => setBarcode(e.target.value));
  };

  const dispatch = useDispatch();
  const {
    setPrintData,
    setWinningPaid,
    setTicketAsCancelled,
    setPrintType,
  } = ticketRedux.actions;
  const { getProfile } = auth.actions;

  const onTicket = (barcode) => {
    handleStopTime();

    setIsLoading(true);
    axios
      .get(`tickets/barcode${barcode ? `?TicketNumber=${barcode}` : ""}`)
      .then(({ data }) => {
        setIsLoading(false);
        setTicket(data);
        dispatch(setPrintData(data));
        const printType =
          data.statusId === 0
            ? 0
            : data.statusId === 2
            ? 1
            : data.statusId === 4
            ? 2
            : data.statusId === 3 && 3;
        dispatch(setPrintType(printType));
        setIsError(false);
        setShowTicket(true);
      })
      .catch(
        ({
          response: {
            data: { message },
          },
        }) => {
          setIsLoading(false);
          setTicket({});
          setIsError(true);
          setShowTicket(false);
          setErrMsg(message);
        }
      );
  };

  const putTicketPaid = (ticketNumber) => {
    setIsLoading(true);
    axios
      .put(`voidTickets/paidTicket`, { ticketNumber })
      .then(() => {
        onTicket(barcode);
        setIsLoading(false);
        setShowPayment(false);
        dispatch(getProfile());
      })
      .catch(
        ({
          response: {
            data: { message },
          },
        }) => {
          setSnackbarType("error");
          if (message === "Your balance is not enough.") {
            setSnackbarMessage("Balansınızda kifayət qədər vəsait yoxdur.");
          }

          handleClick();
          setIsLoading(false);
          setShowPayment(false);
        }
      );
  };

  const putPaidWinningAmount = (ticketNumber) => {
    setIsLoading(true);
    axios
      .put(`tickets/paidWinningAmount`, { ticketNumber })
      .then(() => {
        setSnackbarType("success");
        setSnackbarMessage("Udulmuş məbləğin ödənişi uğurla həyata keçirildi!");
        handleClick();
        setIsLoading(false);
        setShowPayment(false);
        setShowPaidWinning(false);
        setShowPrint(true);
        dispatch(setWinningPaid(true));
        dispatch(setPrintType(1));
      })
      .catch((err) => {
        const message = err.response?.data?.message;
        const status = err.response?.status;
        setSnackbarType("error");
        if (message === "Your balance is not enough.") {
          setSnackbarMessage("Balansınızda kifayət qədər vəsait yoxdur.");
        } else if (status === 401) {
          setSnackbarMessage("İstifadəçinin autentifikasiyası yoxdur.");
        } else {
          setSnackbarMessage(message);
        }

        handleClick();
        setIsLoading(false);
        setShowPaidWinning(false);
      })
      .finally(() => {
        dispatch(getProfile());
      });
  };

  const cancelTicket = (id) => {
    axios
      .put(`tickets/cancelTicket`, { id })
      .then(() => {
        setShowCancelTicket(false);
        setSnackbarType("success");
        setSnackbarMessage("Bilet uğurla ləğv edildi.");
        handleClick();
        setShowPrint(true);
        dispatch(getProfile());
        dispatch(setTicketAsCancelled());
        dispatch(setPrintType(2));
      })
      .catch((err) => {
        setSnackbarType("error");
        const message = err.response?.data?.message ?? "Xəta baş verdi.";
        setSnackbarMessage(message);
        handleClick();
      });
  };

  function MySnackbarContentWrapper(props) {
    const classes = useStyles1();
    const { className, message, onClose, variant, ...other } = props;
    const Icon = variantIcon[variant];

    return (
      <SnackbarContent
        className={clsx(classes[variant], className)}
        aria-describedby="client-snackbar"
        message={
          <span id="client-snackbar" className={classes.message}>
            <Icon className={clsx(classes.icon, classes.iconVariant)} />
            {message}
          </span>
        }
        action={[
          <IconButton
            key="close"
            aria-label="Close"
            color="inherit"
            onClick={onClose}
          >
            <CloseIcon className={classes.icon} />
          </IconButton>,
        ]}
        {...other}
      />
    );
  }

  const [open, setOpen] = useState(false);

  function handleClick() {
    setOpen(true);
  }

  function handleClose(event, reason) {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  }

  const { amber, green } = colors;

  useEffect(() => {
    if (isPrintClosed) {
      setTicket({});
      dispatch(setPrintData({}));
      setShowTicket(false);
    }
  }, [isPrintClosed]);

  useEffect(() => {
    dispatch(setPrintData({}));
  }, [barcode]);

  return (
    <Layout>
      <Ticket
        onTicket={onTicket}
        ticket={ticket}
        barcode={barcode}
        onBarcode={handleBarcode}
        isLoading={isLoading}
        setShowPayment={setShowPayment}
        setShowPaidWinning={setShowPaidWinning}
        setShowEdit={setShowEdit}
        isError={isError}
        errMsg={errMsg}
        setShowCancelTicket={setShowCancelTicket}
        setChooseGroupId={setChooseGroupId}
        setShowPrint={setShowPrint}
        isScanner={isScanner}
        setBarcode={setBarcode}
      />
      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <MySnackbarContentWrapper
          onClose={handleClose}
          variant={snackbarType}
          message={snackbarMessage}
        />
      </Snackbar>
      {showPaidWinning && (
        <PaidWinningAmount
          show={showPaidWinning}
          onHide={setShowPaidWinning}
          onTicket={onTicket}
          take={take}
          skip={skip}
          orderBy={orderBy}
          sortField={sortField}
          barcode={barcode}
          putPaidWinningAmount={putPaidWinningAmount}
        />
      )}
      {showPayment && (
        <Payment
          id={chooseGroupId}
          show={showPayment}
          onHide={setShowPayment}
          onTicket={onTicket}
          skip={skip}
          take={take}
          orderBy={orderBy}
          sortField={sortField}
          barcode={barcode}
          putTicketPaid={putTicketPaid}
        />
      )}
      {showPrint && (
        <Print
          show={showPrint}
          onHide={setShowPrint}
          setIsPrintClosed={setIsPrintClosed}
          setSnackbarType={setSnackbarType}
          setSnackbarMessage={setSnackbarMessage}
        />
      )}
      {showEdit && (
        <EditTicket
          show={showTicket}
          onHide={setShowEdit}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          ticket={ticket}
        />
      )}
      {showCancelTicket && (
        <CancelTicket
          show={showCancelTicket}
          onHide={setShowCancelTicket}
          id={chooseGroupId}
          cancelTicket={cancelTicket}
        />
      )}
    </Layout>
  );
}

export default injectIntl(connect(null, auth.actions)(Tickets));
